/**
 * Clientes (F3a) — porte de src/pages/Clientes do app atual, mesmas regras
 * (packages/core/services/clienteService): CNPJ/CPF validado e único, PJ/PF,
 * segmentos, ativo/inativo, histórico de compras (derivado das vendas).
 */
import { createEffect, createResource, createSignal, For, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { Tabs } from '@ui/components/Tabs'
import type { Cliente, ClienteDetalhe, TipoPessoa } from '@shared/types'
import { clienteSchema } from '@shared/validation'
import { api, versaoDados } from '../nucleo/cliente'
import { mensagemErro } from '../lib/erro'
import { AreaTexto, brl, Campo, criarRecarga, dataBR, Erro, Etiqueta, Formulario, formatarDoc, ListaDetalhe, Marcar, Painel, Selecao, T, Vazio } from '../ui/kit'

const SEGMENTOS = [
  'Moinho', 'Indústria química', 'Silos de grãos', 'Madeireira', 'Alimentos',
  'Farmacêutica', 'Metalurgia', 'Reciclagem', 'Energia/Biomassa', 'Cimento', 'Açúcar e álcool',
]
const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']

export function Clientes(props: { podeEditar: boolean }) {
  const [busca, setBusca] = createSignal('')
  const [segmento, setSegmento] = createSignal('')
  const [uf, setUf] = createSignal('')
  const [inativos, setInativos] = createSignal(false)
  const [recarga, recarregar] = criarRecarga()
  const [detalheId, setDetalheId] = createSignal<number | null>(null)
  const [editando, setEditando] = createSignal<Cliente | null | undefined>(undefined) // undefined = fechado
  const [erro, setErro] = createSignal<string | null>(null)

  const [lista] = createResource(
    () => ({ b: busca(), s: segmento(), u: uf(), i: inativos(), v: versaoDados(), r: recarga() }),
    ({ b, s, u, i }) => api.clientes.list({ busca: b || undefined, segmento: s || undefined, uf: u || undefined, incluirInativos: i }),
  )
  const [segmentos] = createResource(() => ({ v: versaoDados(), r: recarga() }), () => api.clientes.segmentos())

  const alternarAtivo = async (c: Cliente) => {
    setErro(null)
    try {
      await api.clientes.setAtivo(c.id, c.ativo !== 1)
      recarregar()
    } catch (e) {
      setErro(mensagemErro(e))
    }
  }

  const listaEl = (
    <div class="flex flex-col gap-4 p-4 sm:p-6">
      <div class="flex flex-wrap items-center gap-3">
        <div class="mr-auto">
          <h1 class="text-xl font-semibold text-ink">Clientes</h1>
          <p class="text-xs text-ink-subtle">{(lista.latest ?? []).length} cliente(s)</p>
        </div>
        <Show when={props.podeEditar}>
          <Button variant="accent" size="sm" onClick={() => setEditando(null)}>
            Novo cliente
          </Button>
        </Show>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <Input class="w-72 max-w-full" placeholder="Buscar por nome ou CNPJ/CPF…" value={busca()} onInput={(e) => setBusca(e.currentTarget.value)} />
        <Selecao class="w-48" value={segmento()} onChange={(e) => setSegmento(e.currentTarget.value)} aria-label="Segmento">
          <option value="">Todos os segmentos</option>
          <For each={segmentos.latest ?? []}>{(s) => <option value={s}>{s}</option>}</For>
        </Selecao>
        <Input class="w-20" placeholder="UF" value={uf()} onInput={(e) => setUf(e.currentTarget.value.toUpperCase().slice(0, 2))} />
        <Marcar checked={inativos()} onChange={setInativos}>
          Incluir inativos
        </Marcar>
      </div>
      <Erro mensagem={erro()} />
      <Show
        when={(lista.latest ?? []).length > 0}
        fallback={<Vazio titulo={lista.loading ? 'Carregando…' : 'Nenhum cliente encontrado'} texto="Ajuste os filtros ou cadastre um novo cliente." />}
      >
        <div class={T.caixa}>
          <table class={T.tabela}>
            <thead class={T.cab}>
              <tr>
                <th class={T.th}>Cliente</th>
                <th class={T.th}>Documento</th>
                <th class={`${T.th} hidden md:table-cell`}>Segmento</th>
                <th class={`${T.th} hidden lg:table-cell`}>Local</th>
                <th class={T.th}>Status</th>
                <th class={`${T.th} text-right`}>Ações</th>
              </tr>
            </thead>
            <tbody>
              <For each={lista.latest}>
                {(c) => (
                  <tr class={T.tr} classList={{ 'bg-hover': detalheId() === c.id }}>
                    <td class={T.td}>
                      <button type="button" class="text-left font-medium text-ink hover:text-accent" onClick={() => setDetalheId(c.id)}>
                        {c.razaoSocial}
                      </button>
                      <Show when={c.nomeFantasia}>
                        <div class="text-xs text-ink-subtle">{c.nomeFantasia}</div>
                      </Show>
                    </td>
                    <td class={`${T.td} font-mono text-xs text-ink-muted whitespace-nowrap`}>{formatarDoc(c.cnpjCpf, c.tipoPessoa)}</td>
                    <td class={`${T.td} text-ink-muted hidden md:table-cell`}>{c.segmentoIndustria || '—'}</td>
                    <td class={`${T.td} text-ink-muted hidden lg:table-cell`}>{[c.cidade, c.uf].filter(Boolean).join('/') || '—'}</td>
                    <td class={T.td}>
                      <Etiqueta tom={c.ativo ? 'sucesso' : 'neutro'}>{c.ativo ? 'Ativo' : 'Inativo'}</Etiqueta>
                    </td>
                    <td class={`${T.td} text-right whitespace-nowrap`}>
                      <Button variant="ghost" size="sm" onClick={() => setDetalheId(c.id)}>Ver</Button>
                      <Show when={props.podeEditar}>
                        <Button variant="ghost" size="sm" onClick={() => setEditando(c)}>Editar</Button>
                        <Button variant="ghost" size="sm" onClick={() => void alternarAtivo(c)}>{c.ativo ? 'Inativar' : 'Reativar'}</Button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  )

  return (
    <>
      <ListaDetalhe
        id="clientes"
        lista={listaEl}
        detalhe={detalheId() !== null && <DetalheCliente id={detalheId()!} recarga={recarga()} aoFechar={() => setDetalheId(null)} />}
        aoFecharDetalhe={() => setDetalheId(null)}
      />
      <FormCliente
        aberto={editando() !== undefined}
        cliente={editando() ?? null}
        aoFechar={() => setEditando(undefined)}
        aoSalvar={() => (setEditando(undefined), recarregar())}
      />
    </>
  )
}

function Linha(props: { rotulo: string; valor: string | null | undefined; class?: string }) {
  return (
    <div class={props.class}>
      <div class="text-[11px] uppercase tracking-wide text-ink-subtle">{props.rotulo}</div>
      <div class="text-sm text-ink break-words">{props.valor || '—'}</div>
    </div>
  )
}

function DetalheCliente(props: { id: number; recarga: number; aoFechar: () => void }) {
  const [d] = createResource(() => ({ id: props.id, v: versaoDados(), r: props.recarga }), ({ id }) => api.clientes.get(id))
  const [aba, setAba] = createSignal<'dados' | 'historico'>('dados')
  return (
    <Show when={d.latest} fallback={<p class="p-5 text-sm text-ink-subtle">{d.loading ? 'Carregando…' : 'Cliente não encontrado.'}</p>}>
      {(c) => (
        <Painel
          titulo={
            <span class="flex flex-wrap items-center gap-2">
              {c().razaoSocial}
              <Etiqueta tom={c().ativo ? 'sucesso' : 'neutro'}>{c().ativo ? 'Ativo' : 'Inativo'}</Etiqueta>
              <Etiqueta tom="destaque">{c().tipoPessoa}</Etiqueta>
            </span>
          }
          sub={c().nomeFantasia}
          aoFechar={props.aoFechar}
        >
          <Tabs
            aria-label="Seção"
            size="sm"
            value={aba()}
            onChange={setAba}
            options={[
              { value: 'dados', label: 'Dados' },
              { value: 'historico', label: 'Histórico de compras', count: (c() as ClienteDetalhe).historico.length },
            ]}
          />
          <Show when={aba() === 'dados'}>
            <div class="grid grid-cols-2 gap-4">
              <Linha rotulo={c().tipoPessoa === 'PJ' ? 'CNPJ' : 'CPF'} valor={formatarDoc(c().cnpjCpf, c().tipoPessoa)} />
              <Linha rotulo="Inscrição estadual" valor={c().inscricaoEstadual} />
              <Linha rotulo="Segmento" valor={c().segmentoIndustria} />
              <Linha rotulo="Contato" valor={c().contatoPrincipal} />
              <Linha rotulo="Telefone" valor={c().telefone} />
              <Linha rotulo="E-mail" valor={c().email} />
              <Linha class="col-span-2" rotulo="Endereço" valor={[c().endereco, c().cidade, c().uf, c().cep].filter(Boolean).join(', ')} />
              <Show when={c().observacoes}>
                <Linha class="col-span-2" rotulo="Observações" valor={c().observacoes} />
              </Show>
            </div>
          </Show>
          <Show when={aba() === 'historico'}>
            <Show when={c().historico.length > 0} fallback={<Vazio titulo="Nenhuma compra registrada ainda" texto="As vendas deste cliente aparecem aqui automaticamente." />}>
              <div class="text-sm text-ink-muted">
                Total comprado: <span class="font-semibold text-ink">{brl(c().totalCompras)}</span>
              </div>
              <table class={T.tabela}>
                <thead class="text-left text-xs text-ink-subtle">
                  <tr>
                    <th class="py-1 font-medium">Pedido</th>
                    <th class="py-1 font-medium">Data</th>
                    <th class="py-1 font-medium">Status</th>
                    <th class="py-1 font-medium text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={c().historico}>
                    {(h) => (
                      <tr class="border-t border-edge-muted">
                        <td class="py-1.5 text-ink">{h.numeroPedido}</td>
                        <td class="py-1.5 text-ink-muted">{dataBR(h.dataVenda)}</td>
                        <td class="py-1.5"><Etiqueta>{h.status}</Etiqueta></td>
                        <td class="py-1.5 text-right tabular-nums text-ink">{brl(h.valorTotal)}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </Show>
          </Show>
        </Painel>
      )}
    </Show>
  )
}

type Form = {
  tipoPessoa: TipoPessoa
  razaoSocial: string; nomeFantasia: string; cnpjCpf: string; inscricaoEstadual: string; segmentoIndustria: string
  endereco: string; cidade: string; uf: string; cep: string; telefone: string; email: string; contatoPrincipal: string; observacoes: string
}
const formDe = (c: Cliente | null): Form => ({
  tipoPessoa: c?.tipoPessoa ?? 'PJ',
  razaoSocial: c?.razaoSocial ?? '', nomeFantasia: c?.nomeFantasia ?? '', cnpjCpf: c?.cnpjCpf ?? '',
  inscricaoEstadual: c?.inscricaoEstadual ?? '', segmentoIndustria: c?.segmentoIndustria ?? '',
  endereco: c?.endereco ?? '', cidade: c?.cidade ?? '', uf: c?.uf ?? '', cep: c?.cep ?? '',
  telefone: c?.telefone ?? '', email: c?.email ?? '', contatoPrincipal: c?.contatoPrincipal ?? '', observacoes: c?.observacoes ?? '',
})

function FormCliente(props: { aberto: boolean; cliente: Cliente | null; aoFechar: () => void; aoSalvar: () => void }) {
  const [f, setF] = createSignal<Form>(formDe(null))
  const [erros, setErros] = createSignal<Record<string, string>>({})
  const [erroGeral, setErroGeral] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  createEffect(on(() => [props.aberto, props.cliente] as const, ([a, c]) => a && (setF(formDe(c)), setErros({}), setErroGeral(null))))
  const set = <K extends keyof Form>(k: K) => (e: { currentTarget: { value: string } }) => setF({ ...f(), [k]: e.currentTarget.value as Form[K] })

  const salvar = async () => {
    setErros({})
    setErroGeral(null)
    // mesma validação do app atual (Zod compartilhado); o núcleo valida de novo ao gravar
    const r = clienteSchema.safeParse(f())
    if (!r.success) {
      const e: Record<string, string> = {}
      for (const i of r.error.issues) e[String(i.path[0])] = i.message
      return setErros(e)
    }
    setSalvando(true)
    try {
      if (props.cliente) await api.clientes.update(props.cliente.id, r.data)
      else await api.clientes.create(r.data)
      props.aoSalvar()
    } catch (e) {
      setErroGeral(mensagemErro(e))
    } finally {
      setSalvando(false)
    }
  }
  const pj = () => f().tipoPessoa === 'PJ'
  return (
    <Formulario
      aberto={props.aberto}
      titulo={props.cliente ? 'Editar cliente' : 'Novo cliente'}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando()}>{salvando() ? 'Salvando…' : 'Salvar'}</Button>
        </>
      }
    >
      <Erro mensagem={erroGeral()} />
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Campo rotulo="Tipo">
          <Selecao value={f().tipoPessoa} onChange={set('tipoPessoa')}>
            <option value="PJ">Pessoa Jurídica</option>
            <option value="PF">Pessoa Física</option>
          </Selecao>
        </Campo>
        <Campo class="sm:col-span-3" rotulo={pj() ? 'Razão social' : 'Nome'} erro={erros().razaoSocial}>
          <Input value={f().razaoSocial} onInput={set('razaoSocial')} />
        </Campo>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <Campo rotulo="Nome fantasia">
          <Input value={f().nomeFantasia} onInput={set('nomeFantasia')} />
        </Campo>
        <Campo rotulo={pj() ? 'CNPJ' : 'CPF'} erro={erros().cnpjCpf}>
          <Input value={f().cnpjCpf} onInput={set('cnpjCpf')} placeholder={pj() ? '00.000.000/0000-00' : '000.000.000-00'} />
        </Campo>
      </div>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Campo rotulo="Inscrição estadual">
          <Input value={f().inscricaoEstadual} onInput={set('inscricaoEstadual')} />
        </Campo>
        <Campo rotulo="Segmento da indústria">
          <Input value={f().segmentoIndustria} onInput={set('segmentoIndustria')} list="segmentos-werc" />
        </Campo>
        <Campo rotulo="Contato principal">
          <Input value={f().contatoPrincipal} onInput={set('contatoPrincipal')} />
        </Campo>
      </div>
      <datalist id="segmentos-werc">
        <For each={SEGMENTOS}>{(s) => <option value={s} />}</For>
      </datalist>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Campo class="col-span-2" rotulo="Endereço">
          <Input value={f().endereco} onInput={set('endereco')} />
        </Campo>
        <Campo rotulo="Cidade">
          <Input value={f().cidade} onInput={set('cidade')} />
        </Campo>
        <Campo rotulo="UF">
          <Selecao value={f().uf} onChange={set('uf')}>
            <option value="">—</option>
            <For each={UFS}>{(u) => <option value={u}>{u}</option>}</For>
          </Selecao>
        </Campo>
      </div>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Campo rotulo="CEP">
          <Input value={f().cep} onInput={set('cep')} />
        </Campo>
        <Campo rotulo="Telefone">
          <Input value={f().telefone} onInput={set('telefone')} />
        </Campo>
        <Campo rotulo="E-mail" erro={erros().email}>
          <Input value={f().email} onInput={set('email')} />
        </Campo>
      </div>
      <Campo rotulo="Observações">
        <AreaTexto value={f().observacoes} onInput={set('observacoes')} />
      </Campo>
    </Formulario>
  )
}
